// ============================================
// FILE: src/components/CartScreen.jsx
// ============================================
//
// Marketplace-style bag page: item list on the left, a sticky price panel on
// the right (desktop) or pinned to the bottom (mobile).
//
// The props contract is unchanged — updateQuantity(lineKey, delta) and
// removeFromCart(lineKey) are still keyed by `item.cartKey`, which is
// `${id}::${variant_id}`. Two sizes of the same product stay two lines.

import React, { useState } from 'react';
import { ShoppingBag, Tag, X, ChevronDown, RotateCcw, ArrowLeft, Check } from 'lucide-react';
import { resolveProductImageUrl } from '../utils/imageHelpers';

const STEPS = ['Bag', 'Address', 'Payment'];

/** "Delivered by 27 Aug" — a standard 4-day promise, computed client-side. */
const deliveryEstimate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 4);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const CartScreen = ({
  cart,
  updateQuantity,
  removeFromCart,
  getCartTotal,
  getCartCount,
  handleCheckout,
  // Coupon props
  couponCode,
  setCouponCode,
  appliedCoupon,
  couponDiscount,
  couponError,
  handleApplyCoupon,
  handleRemoveCoupon,
  getFinalTotal,
  getHandlingCharge,
  getSuggestedCoupons,
  // Settings
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50 },
  setActiveTab,
  goBack,
  appTheme = 'blue',
  isDesktop = false,
}) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const suggestedCoupons = getSuggestedCoupons ? getSuggestedCoupons() : [];

  const lineKeyOf = (item) => item.cartKey || item.id;

  // ---- Price breakdown -------------------------------------------------- //
  const totalMrp = cart.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0);

  const mrpDiscount = cart.reduce((sum, item) => {
    const sale = Number(item.sale_price);
    const price = Number(item.price);
    return sale > 0 && sale < price ? sum + (price - sale) * item.quantity : sum;
  }, 0);

  const subtotal = Number(getCartTotal()) || 0;
  const handlingCharge = typeof getHandlingCharge === 'function' ? Number(getHandlingCharge()) || 0 : 0;
  const qualifiesFreeShipping = subtotal >= appSettings.freeShippingThreshold;
  const shippingFee = qualifiesFreeShipping ? 0 : Number(appSettings.standardShippingFee) || 0;
  const tax = subtotal * ((Number(appSettings.taxRate) || 0) / 100);
  const grandTotal = getFinalTotal ? Number(getFinalTotal()) : subtotal;
  const totalSavings = mrpDiscount + Number(couponDiscount || 0) + (qualifiesFreeShipping ? Number(appSettings.standardShippingFee) || 0 : 0);
  const remainingForFreeShipping = appSettings.freeShippingThreshold - (subtotal - (couponDiscount || 0));

  // ---- Empty state ------------------------------------------------------ //
  if (!cart || cart.length === 0) {
    return (
      <div className="fashion-scope min-h-screen bg-white pb-20">
        <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <button
            onClick={() => (goBack ? goBack() : setActiveTab('home'))}
            className="p-1 -ml-1"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label">Shopping Bag</span>
        </div>

        <div className="flex flex-col items-center justify-center text-center px-6 pt-24">
          <ShoppingBag size={56} strokeWidth={1.2} style={{ color: 'var(--fx-ink-3)' }} />
          <h3 className="mt-5" style={{ fontSize: 18, fontWeight: 700, color: 'var(--fx-ink)' }}>
            Hey, it feels so light!
          </h3>
          <p className="mt-1.5" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
            There is nothing in your bag. Let's add some items.
          </p>
          <button
            onClick={() => setActiveTab('home')}
            className="fx-btn fx-btn-ghost mt-6 px-12"
            style={{ color: 'var(--fx-pink)', borderColor: 'var(--fx-pink)' }}
          >
            Shop Now
          </button>
        </div>
      </div>
    );
  }

  // ---- Item card -------------------------------------------------------- //
  const renderItem = (item) => {
    const lineKey = lineKeyOf(item);
    const price = Number(item.price) || 0;
    const sale = Number(item.sale_price);
    const hasDiscount = sale > 0 && sale < price;
    const unitPrice = hasDiscount ? sale : price;
    const offPercent = hasDiscount ? Math.round(((price - sale) / price) * 100) : 0;
    const image = resolveProductImageUrl(
      item.featured_image_url || item.image_url || item.featured_image || item.image || item.thumbnail_url
    );

    return (
      <div
        key={lineKey}
        className="relative flex gap-3 p-3 bg-white"
        style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}
      >
        <div
          className="flex-shrink-0 bg-[#f5f5f6] overflow-hidden"
          style={{ width: 96, height: 128, borderRadius: 2 }}
        >
          {image ? (
            <img src={image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl">
              {item.icon || '🛍️'}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <p className="truncate" style={{ fontSize: 15, fontWeight: 700, color: 'var(--fx-ink)' }}>
            {item.brand || item.name}
          </p>
          {item.brand && (
            <p className="truncate" style={{ fontSize: 13, color: 'var(--fx-ink-2)', marginTop: 2 }}>
              {item.name}
            </p>
          )}

          {/* Size and quantity chips — size is fixed here because changing it
              means a different variant, i.e. a different cart line. */}
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            {item.variant_name && (
              <span
                className="inline-flex items-center px-2.5 py-1"
                style={{ background: '#f5f5f6', borderRadius: 2, fontSize: 13, fontWeight: 700, color: 'var(--fx-ink)' }}
              >
                Size: {item.variant_name}
              </span>
            )}

            <span
              className="relative inline-flex items-center gap-1 px-2.5 py-1"
              style={{ background: '#f5f5f6', borderRadius: 2, fontSize: 13, fontWeight: 700, color: 'var(--fx-ink)' }}
            >
              Qty: {item.quantity}
              <ChevronDown size={13} />
              <select
                value={item.quantity}
                onChange={(e) => {
                  const delta = Number(e.target.value) - item.quantity;
                  if (delta !== 0) updateQuantity(lineKey, delta);
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label={`Quantity for ${item.name}`}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </span>
          </div>

          <div className="flex items-baseline gap-2 mt-2.5 flex-wrap">
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--fx-ink)' }}>
              ₹{(unitPrice * item.quantity).toFixed(0)}
            </span>
            {hasDiscount && (
              <>
                <span style={{ fontSize: 13, color: 'var(--fx-ink-3)', textDecoration: 'line-through' }}>
                  ₹{(price * item.quantity).toFixed(0)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--fx-off)' }}>{offPercent}% OFF</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-2">
            <RotateCcw size={13} style={{ color: 'var(--fx-ink-3)' }} />
            <span style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
              15 days return · Delivered by{' '}
              <span style={{ fontWeight: 700, color: 'var(--fx-ink)' }}>{deliveryEstimate()}</span>
            </span>
          </div>
        </div>

        <button
          onClick={() => removeFromCart(lineKey)}
          className="absolute top-2 right-2 p-1"
          aria-label={`Remove ${item.name} from bag`}
        >
          <X size={16} style={{ color: 'var(--fx-ink-2)' }} />
        </button>
      </div>
    );
  };

  // ---- Coupons ---------------------------------------------------------- //
  const couponBlock = (
    <div className="p-4 bg-white" style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
      <h3 className="fx-label mb-3">Coupons</h3>

      {appliedCoupon ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--fx-green)' }}
            >
              <Check size={14} className="text-white" />
            </span>
            <div className="min-w-0">
              <p className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{appliedCoupon.code}</p>
              <p style={{ fontSize: 13, color: 'var(--fx-green)' }}>You saved ₹{couponDiscount}</p>
            </div>
          </div>
          <button
            onClick={handleRemoveCoupon}
            style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
          >
            REMOVE
          </button>
        </div>
      ) : (
        <>
          <button
            onClick={() => setShowCouponInput(!showCouponInput)}
            className="w-full flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2.5">
              <Tag size={16} style={{ color: 'var(--fx-ink-2)' }} />
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Apply Coupons</span>
            </span>
            <span
              className="px-3 py-1.5"
              style={{ border: '1px solid var(--fx-pink)', borderRadius: 2, fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
            >
              APPLY
            </span>
          </button>

          {showCouponInput && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="flex-1 px-3 py-2 outline-none"
                  style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 14 }}
                />
                <button
                  onClick={handleApplyCoupon}
                  className="px-5 text-white"
                  style={{ background: 'var(--fx-pink)', borderRadius: 2, fontSize: 13, fontWeight: 700 }}
                >
                  APPLY
                </button>
              </div>

              {couponError && (
                <p className="flex items-center gap-1" style={{ fontSize: 13, color: 'var(--fx-pink)' }}>
                  <X size={14} />
                  {couponError}
                </p>
              )}

              {suggestedCoupons.length > 0 && (
                <div className="space-y-2 pt-1">
                  {suggestedCoupons.map((coupon) => (
                    <button
                      key={coupon.code}
                      onClick={() => { setCouponCode(coupon.code); handleApplyCoupon(); }}
                      className="w-full text-left p-3 flex items-center justify-between gap-3"
                      style={{ border: '1px dashed var(--fx-line)', borderRadius: 2 }}
                    >
                      <span className="min-w-0">
                        <span className="block truncate" style={{ fontSize: 14, fontWeight: 700 }}>
                          {coupon.code}
                        </span>
                        <span className="block truncate" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                          {coupon.description}
                        </span>
                      </span>
                      <span
                        className="flex-shrink-0 px-2 py-0.5"
                        style={{ background: '#e8f7f3', color: 'var(--fx-green)', borderRadius: 2, fontSize: 12, fontWeight: 700 }}
                      >
                        {coupon.type === 'percentage' ? `${coupon.discount}%` : `₹${coupon.discount}`} OFF
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ---- Price panel ------------------------------------------------------ //
  const priceRow = (label, value, { color, strike, bold } = {}) => (
    <div className="flex items-center justify-between py-1.5">
      <span style={{ fontSize: 14, color: bold ? 'var(--fx-ink)' : 'var(--fx-ink-2)', fontWeight: bold ? 700 : 400 }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: bold ? 700 : 400,
          color: color || 'var(--fx-ink)',
          textDecoration: strike ? 'line-through' : 'none',
        }}
      >
        {value}
      </span>
    </div>
  );

  const priceDetails = (
    <div className="p-4 bg-white" style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
      <h3 className="fx-label mb-2">
        Price Details ({getCartCount()} {getCartCount() === 1 ? 'Item' : 'Items'})
      </h3>

      {priceRow('Total MRP', `₹${totalMrp.toFixed(0)}`)}
      {mrpDiscount > 0 && priceRow('Discount on MRP', `-₹${mrpDiscount.toFixed(0)}`, { color: 'var(--fx-green)' })}
      {appliedCoupon && priceRow(`Coupon (${appliedCoupon.code})`, `-₹${couponDiscount}`, { color: 'var(--fx-green)' })}
      {handlingCharge > 0 && priceRow('Platform Fee', `₹${handlingCharge.toFixed(0)}`)}
      {tax > 0 && priceRow(`GST (${appSettings.taxRate}%)`, `₹${tax.toFixed(0)}`)}

      <div className="flex items-center justify-between py-1.5">
        <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Shipping Fee</span>
        <span style={{ fontSize: 14 }}>
          {qualifiesFreeShipping || shippingFee === 0 ? (
            <>
              {Number(appSettings.standardShippingFee) > 0 && (
                <span style={{ color: 'var(--fx-ink-3)', textDecoration: 'line-through', marginRight: 6 }}>
                  ₹{appSettings.standardShippingFee}
                </span>
              )}
              <span style={{ color: 'var(--fx-green)', fontWeight: 700 }}>FREE</span>
            </>
          ) : (
            `₹${shippingFee.toFixed(0)}`
          )}
        </span>
      </div>

      <div className="mt-2 pt-3" style={{ borderTop: '1px solid var(--fx-line)' }}>
        {priceRow('Total Amount', `₹${grandTotal.toFixed(0)}`, { bold: true })}
      </div>

      {isDesktop && (
        <button onClick={handleCheckout} className="fx-btn fx-btn-primary w-full mt-4">
          Place Order
        </button>
      )}
    </div>
  );

  // ---- Banners ---------------------------------------------------------- //
  const savingsBanner = totalSavings > 0 && (
    <div
      className="px-4 py-2.5"
      style={{ background: '#e8f7f3', borderRadius: 4, border: '1px solid #b7e5d8' }}
    >
      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--fx-green)' }}>
        You are saving ₹{totalSavings.toFixed(0)} on this order
      </p>
    </div>
  );

  // No nudge when the delivery zone never ships free (infinite threshold) or already ships free.
  const shippingNudge = !qualifiesFreeShipping && shippingFee > 0
    && Number.isFinite(remainingForFreeShipping) && remainingForFreeShipping > 0 && (
    <div
      className="px-4 py-2.5"
      style={{ background: '#fff4f6', borderRadius: 4, border: '1px solid #ffd0da' }}
    >
      <p style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
        Add items worth{' '}
        <span style={{ fontWeight: 700, color: 'var(--fx-pink)' }}>₹{remainingForFreeShipping.toFixed(0)}</span>
        {' '}more for FREE delivery
      </p>
    </div>
  );

  // ---- Step indicator --------------------------------------------------- //
  const steps = (
    <div className="flex items-center justify-center gap-2 py-3" style={{ borderBottom: '1px solid var(--fx-line)' }}>
      {STEPS.map((step, index) => (
        <React.Fragment key={step}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: index === 0 ? 'var(--fx-ink)' : 'var(--fx-ink-3)',
            }}
          >
            {step}
          </span>
          {index < STEPS.length - 1 && (
            <span style={{ width: 28, height: 1, background: 'var(--fx-line)' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ---- Layout ----------------------------------------------------------- //
  return (
    <div className="fashion-scope min-h-screen bg-[#fafafa] pb-24">
      <div className="bg-white">
        <div className={`flex items-center gap-3 px-4 py-3 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}>
          <button
            onClick={() => (goBack ? goBack() : setActiveTab('home'))}
            className="p-1 -ml-1"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label">Shopping Bag</span>
          <span style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
            {getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className={isDesktop ? 'max-w-[1200px] mx-auto' : ''}>{steps}</div>
      </div>

      <div className={`${isDesktop ? 'max-w-[1200px] mx-auto flex gap-6 px-4' : 'px-3'} pt-4`}>
        <div className={isDesktop ? 'flex-1 min-w-0 space-y-3' : 'space-y-3'}>
          {savingsBanner}
          {shippingNudge}
          {cart.map(renderItem)}
          {!isDesktop && couponBlock}
          {!isDesktop && priceDetails}
        </div>

        {isDesktop && (
          <div className="w-[340px] flex-shrink-0 space-y-3 self-start sticky top-4">
            {couponBlock}
            {priceDetails}
          </div>
        )}
      </div>

      {/* Mobile action bar */}
      {!isDesktop && (
        <div
          className="fixed inset-x-0 bottom-0 bg-white flex items-center gap-3 px-3 py-2.5"
          style={{ borderTop: '1px solid var(--fx-line)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          <div className="flex-shrink-0">
            <p style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>₹{grandTotal.toFixed(0)}</p>
            {totalSavings > 0 && (
              <p style={{ fontSize: 11, color: 'var(--fx-green)' }}>
                Saved ₹{totalSavings.toFixed(0)}
              </p>
            )}
          </div>
          <button onClick={handleCheckout} className="fx-btn fx-btn-primary flex-1">
            Place Order
          </button>
        </div>
      )}
    </div>
  );
};

export default CartScreen;
